import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { USER_SPORTS_OPTIONS } from '../types/models';
import {
  CheckCircle2,
  Clock3,
  Image as ImageIcon,
  Languages,
  Loader2,
  Mail,
  Paperclip,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  Users
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { r2Storage } from '../services/storage/r2Storage';
import {
  DEFAULT_EMAIL_LANGUAGE,
  EMAIL_LANGUAGE_OPTIONS,
  EmailContextResponse,
  EmailLanguage,
  EmailRecipientFilters,
  EmailRecipientMode,
  RECIPIENT_LANGUAGE_OPTIONS,
  normalizeRecipientLanguage,
  SPORT_IMAGE_OPTIONS,
  SPORT_IMAGE_TEMPLATE_IDS,
  SendBulkEmailResponse,
  emailService,
  parseCustomRecipientEmails
} from '../services/emailService';

const formatHistoryTime = (value?: any) => {
  if (!value) return 'Unknown time';

  try {
    if (typeof value === 'object') {
      if (typeof value.toDate === 'function') {
        const d = value.toDate();
        return Number.isNaN(d.getTime()) ? 'Unknown time' : d.toLocaleString();
      }

      const secs = typeof value._seconds === 'number' ? value._seconds : (typeof value.seconds === 'number' ? value.seconds : null);
      if (secs !== null) {
        const d = new Date(secs * 1000);
        return Number.isNaN(d.getTime()) ? 'Unknown time' : d.toLocaleString();
      }

      if (value.timestampValue) {
        const d = new Date(value.timestampValue);
        return Number.isNaN(d.getTime()) ? 'Unknown time' : d.toLocaleString();
      }
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'Unknown time';
    }

    return parsed.toLocaleString();
  } catch (error) {
    return 'Unknown time';
  }
};

const formatHistoryMode = (value?: EmailRecipientMode) => {
  if (value === 'single') return 'Single user';
  if (value === 'selected') return 'Selected users';
  return 'All in scope';
};

const formatLanguageLabel = (value?: EmailLanguage) => {
  const option = EMAIL_LANGUAGE_OPTIONS.find((entry) => entry.value === value);
  return option ? option.label : 'English';
};

const formatSportImageLabel = (value?: string) => {
  const option = SPORT_IMAGE_OPTIONS.find((entry) => entry.value === value);
  return option ? option.label : value || '';
};

const formatRecipientLocation = (user: EmailContextResponse['recipientSummary']['users'][number]) => {
  return [user.city, user.state, user.location].filter(Boolean).join(', ') || 'Location unavailable';
};

const DEFAULT_RECIPIENT_FILTERS: EmailRecipientFilters = {
  includeNotVerified: false,
  includeNewUsers: false,
  includeVerifiers: false,
  sports: [],
  accountTypes: [],
  locations: [],
  languages: []
};
const RECIPIENT_PLACEHOLDER_PATTERN = /\{\{\s*(?:name|user)\s*\}\}/i;

interface FixedEmailTemplateContent {
  subject: string;
  body: string;
}

interface FixedEmailTemplate {
  id: string;
  name: string;
  description: string;
  content: Record<EmailLanguage, FixedEmailTemplateContent>;
}

type EmailCenterTab = 'compose' | 'history';

const FIXED_EMAIL_TEMPLATES: FixedEmailTemplate[] = [
  {
    id: 'welcome-to-amaplayer',
    name: 'Welcome To AmaPlayer',
    description: 'Onboarding email for newly added users.',
    content: {
      en: {
        subject: 'Welcome to AmaPlayer',
        body: `Hi {{name}},

Welcome to AmaPlayer.

We are excited to have you in our sports community. You can now discover players, connect with coaches, and participate in upcoming activities from your region.

Complete your profile and start exploring opportunities today.

Regards,
AmaPlayer Team`
      },
      hi: {
        subject: 'AmaPlayer में आपका स्वागत है',
        body: `नमस्ते {{name}},

AmaPlayer में आपका स्वागत है।

हमें खुशी है कि आप हमारी स्पोर्ट्स कम्युनिटी का हिस्सा हैं। अब आप खिलाड़ियों को खोज सकते हैं, कोच से जुड़ सकते हैं और अपने क्षेत्र की आगामी गतिविधियों में हिस्सा ले सकते हैं।

अपनी प्रोफ़ाइल पूरी करें और आज ही नए अवसर तलाशना शुरू करें।

सादर,
AmaPlayer टीम`
      }
    }
  },
  {
    id: 'introduction',
    name: 'Introduction',
    description: 'Quick introduction to platform benefits and next steps.',
    content: {
      en: {
        subject: 'Introducing AmaPlayer Community',
        body: `Hi {{name}},

AmaPlayer helps athletes, coaches, and verifiers stay connected in one place.

You can use the platform to:
- Build your profile
- Join events in your area
- Track verification progress
- Connect with coaches and peers

If you need support, reply to this email and our team will help.

Regards,
AmaPlayer Team`
      },
      hi: {
        subject: 'AmaPlayer कम्युनिटी से परिचय',
        body: `नमस्ते {{name}},

AmaPlayer खिलाड़ियों, कोच और वेरिफ़ायर को एक ही जगह जोड़े रखता है।

आप इस प्लेटफ़ॉर्म पर ये कर सकते हैं:
- अपनी प्रोफ़ाइल बनाएं
- अपने क्षेत्र के इवेंट में शामिल हों
- वेरिफ़िकेशन की प्रगति ट्रैक करें
- कोच और साथी खिलाड़ियों से जुड़ें

अगर आपको मदद चाहिए, तो इस ईमेल का जवाब दें, हमारी टीम आपकी सहायता करेगी।

सादर,
AmaPlayer टीम`
      }
    }
  },
  {
    id: 'peer-verification',
    name: 'Peer Verification',
    description: 'Request users to complete peer verification.',
    content: {
      en: {
        subject: 'Peer Verification Request',
        body: `Hi {{name}},

Your peer verification is pending.

Please complete the verification process to improve your profile trust score and unlock more opportunities.

Open AmaPlayer and complete verification at the earliest.

Regards,
AmaPlayer Team`
      },
      hi: {
        subject: 'पीयर वेरिफ़िकेशन अनुरोध',
        body: `नमस्ते {{name}},

आपका पीयर वेरिफ़िकेशन अभी बाकी है।

कृपया वेरिफ़िकेशन प्रक्रिया पूरी करें ताकि आपकी प्रोफ़ाइल का ट्रस्ट स्कोर बेहतर हो और नए अवसर खुलें।

AmaPlayer खोलें और जल्द से जल्द वेरिफ़िकेशन पूरा करें।

सादर,
AmaPlayer टीम`
      }
    }
  },
  {
    id: 'new-event-near-you',
    name: 'New Event Near You',
    description: 'Notify users about nearby events.',
    content: {
      en: {
        subject: 'New Event Near You',
        body: `Hi {{name}},

A new event has been announced near your location.

Check the Events section in AmaPlayer to view event details, registration timeline, and participation criteria.

Register early to secure your slot.

Regards,
AmaPlayer Team`
      },
      hi: {
        subject: 'आपके पास नया इवेंट',
        body: `नमस्ते {{name}},

आपके क्षेत्र के पास एक नया इवेंट घोषित हुआ है।

इवेंट की जानकारी, रजिस्ट्रेशन की समय-सीमा और भागीदारी की शर्तें देखने के लिए AmaPlayer का इवेंट्स सेक्शन खोलें।

अपनी जगह पक्की करने के लिए जल्दी रजिस्टर करें।

सादर,
AmaPlayer टीम`
      }
    }
  },
  {
    id: 'missing-top-players-coaches',
    name: 'Missing Top Players For Coaches',
    description: 'Prompt coaches to review high-potential players.',
    content: {
      en: {
        subject: 'You Are Missing Top Players For Coaches',
        body: `Hi {{name}},

We found high-potential players in your region who may match your selection criteria.

Visit AmaPlayer to review recommended profiles and connect with top players before upcoming selections.

Regards,
AmaPlayer Team`
      },
      hi: {
        subject: 'आप टॉप खिलाड़ियों से चूक रहे हैं',
        body: `नमस्ते {{name}},

हमें आपके क्षेत्र में ऐसे होनहार खिलाड़ी मिले हैं जो आपके चयन मानदंडों से मेल खा सकते हैं।

AmaPlayer पर जाकर सुझाई गई प्रोफ़ाइल देखें और आगामी चयन से पहले टॉप खिलाड़ियों से जुड़ें।

सादर,
AmaPlayer टीम`
      }
    }
  },
  {
    id: 'new-user-profile-completion',
    name: 'New User Profile Completion',
    description: 'Uses the rich HTML template from the email templates folder.',
    content: {
      en: {
        subject: 'Complete Your AmaPlayer Profile In 2 Minutes',
        body: `Hi {{name}},

Here’s what you can do in just 2 minutes:

Complete all the steps to get better visibility and better reach to scouts and sponsors.

AmaPlayer is built to give grassroots players real visibility, and your journey starts only when you become active.

Complete your profile now.

Let’s get you noticed.

Team AmaPlayer
AmaPlayer
MY COUNTRY MY GAME`
      },
      hi: {
        subject: 'सिर्फ़ 2 मिनट में AmaPlayer प्रोफ़ाइल पूरी करें',
        body: `नमस्ते {{name}},

सिर्फ़ 2 मिनट में आप ये कर सकते हैं:

सभी चरण पूरे करें ताकि आपको बेहतर विज़िबिलिटी मिले और स्काउट्स व स्पॉन्सर तक आपकी पहुंच बढ़े।

AmaPlayer इसलिए बनाया गया है कि ज़मीनी स्तर के खिलाड़ियों को असली पहचान मिले, और आपका सफर तभी शुरू होता है जब आप सक्रिय होते हैं।

अभी अपनी प्रोफ़ाइल पूरी करें।

आइए, आपको पहचान दिलाते हैं।

टीम AmaPlayer
AmaPlayer
MY COUNTRY MY GAME`
      }
    }
  },
  {
    id: 'complete-profile-player',
    name: 'Complete Profile (Player)',
    description: 'Detailed profile-completion pitch addressed to the player. Supports an example profile card.',
    content: {
      en: {
        subject: 'Complete Your Profile, Unlock Your Digital Sporting Identity',
        body: `Dear Player,

You have taken the first step by joining AmaPlayer. Now, take two minutes to complete your profile and unlock your full digital sporting identity.

A complete AmaPlayer profile brings your entire sports journey into one unified platform:

Your Sport, Position & Skills: Showcase your primary sport, playing role, dominant hand/foot, and key athletic abilities.

Live Scoring & Detailed Match Logs: Record every match outcome, live play-by-play actions, and individual performance milestones directly to your account.

Daily Training Logs: Track practice routines, drills, workout sessions, and fitness metrics to monitor your growth over time.

Past & Upcoming Career History: Maintain a permanent timeline of every team you have represented, past tournament campaigns, and upcoming fixtures or trials.

Trophy Cabinet & Highlights: Upload match videos, tournament photos, medals, certificates, and physical metrics in one organized portfolio.

Tournament & Trial Alerts: Find, track, and register directly for local tournaments, school meets, and trials happening near you.

Scout & Academy Visibility: Put your abilities in front of verified coaches, club selectors, and academies actively scouting grassroots talent.

Verified Endorsements: Add official coach feedback and club verification to stand out from unverified profiles.

An incomplete profile containing only your name and phone number cannot properly represent your talent. Give coaches, scouts, and organizers the full picture of what you bring to the field.

We have attached an example profile so you can see how your completed AmaPlayer profile looks.

Complete Your Profile Today:
https://amaplayer.com/profile

Your talent deserves more than a name on a list - it deserves to be seen, recorded, and remembered.

Regards,
Team AmaPlayer
Mera Desh, Mera Khel`
      },
      hi: {
        subject: 'अपनी प्रोफ़ाइल पूरी करें, अपनी डिजिटल स्पोर्टिंग पहचान अनलॉक करें',
        body: `प्रिय खिलाड़ी,

AmaPlayer से जुड़कर आपने पहला कदम उठा लिया है। अब सिर्फ़ दो मिनट लगाकर अपनी प्रोफ़ाइल पूरी करें और अपनी पूरी डिजिटल स्पोर्टिंग पहचान अनलॉक करें।

पूरी AmaPlayer प्रोफ़ाइल आपके पूरे खेल सफर को एक ही प्लेटफ़ॉर्म पर ले आती है:

आपका खेल, पोज़िशन और स्किल्स: अपना मुख्य खेल, खेलने की भूमिका, प्रमुख हाथ/पैर और मुख्य एथलेटिक क्षमताएं दिखाएं।

लाइव स्कोरिंग और विस्तृत मैच लॉग: हर मैच का नतीजा, लाइव बॉल-दर-बॉल गतिविधि और व्यक्तिगत प्रदर्शन के मुकाम सीधे अपने अकाउंट में दर्ज करें।

दैनिक ट्रेनिंग लॉग: प्रैक्टिस रूटीन, ड्रिल्स, वर्कआउट सेशन और फ़िटनेस मेट्रिक्स ट्रैक करें और समय के साथ अपनी प्रगति देखें।

पिछला और आगामी करियर इतिहास: हर उस टीम का स्थायी रिकॉर्ड रखें जिसका आपने प्रतिनिधित्व किया, पिछले टूर्नामेंट और आने वाले मैच या ट्रायल।

ट्रॉफ़ी कैबिनेट और हाइलाइट्स: मैच वीडियो, टूर्नामेंट फ़ोटो, मेडल, सर्टिफ़िकेट और फ़िज़िकल मेट्रिक्स एक व्यवस्थित पोर्टफ़ोलियो में अपलोड करें।

टूर्नामेंट और ट्रायल अलर्ट: अपने आस-पास हो रहे स्थानीय टूर्नामेंट, स्कूल मीट और ट्रायल खोजें, ट्रैक करें और सीधे रजिस्टर करें।

स्काउट और अकादमी विज़िबिलिटी: अपनी क्षमताओं को वेरिफ़ाइड कोच, क्लब सेलेक्टर और ज़मीनी प्रतिभा खोज रही अकादमियों के सामने रखें।

वेरिफ़ाइड एंडोर्समेंट: आधिकारिक कोच फ़ीडबैक और क्लब वेरिफ़िकेशन जोड़ें ताकि आपकी प्रोफ़ाइल बाकियों से अलग दिखे।

सिर्फ़ नाम और फ़ोन नंबर वाली अधूरी प्रोफ़ाइल आपकी प्रतिभा को सही ढंग से नहीं दिखा सकती। कोच, स्काउट और आयोजकों को यह पूरी तस्वीर दें कि आप मैदान पर क्या ला सकते हैं।

हमने एक उदाहरण प्रोफ़ाइल संलग्न की है ताकि आप देख सकें कि आपकी पूरी AmaPlayer प्रोफ़ाइल कैसी दिखेगी।

आज ही अपनी प्रोफ़ाइल पूरी करें:
https://amaplayer.com/profile

आपकी प्रतिभा सिर्फ़ एक सूची में नाम से कहीं ज़्यादा की हकदार है - वह देखी जाने, दर्ज होने और याद रखे जाने की हकदार है।

सादर,
टीम AmaPlayer
मेरा देश, मेरा खेल`
      }
    }
  },
  {
    id: 'complete-profile-parent',
    name: 'Complete Profile (Parent)',
    description: 'Profile-completion pitch addressed to the parent. Supports an example profile card.',
    content: {
      en: {
        subject: "Help Complete Your Child's AmaPlayer Profile",
        body: `Dear Parent,

Your child has taken the first step by registering on AmaPlayer, but their profile is still incomplete. We request you to take two minutes to help them complete it and secure their digital sports identity.

Young athletes spend years practicing, competing, and representing their school or academy. However, match performances, certificates, and milestones often remain scattered across old phones, scorebooks, or forgotten folders - and are eventually lost.

AmaPlayer builds a permanent, verified digital sporting passport that preserves and showcases your child's complete journey in one place:

Sport, Playing Position & Key Skills: Highlight your child's primary sport, field role, dominant hand/foot, and core athletic strengths.

Live Scoring & Detailed Match Logs: Maintain a verified match history with official live scorecards, stats, and game-by-game performances.

Daily Training & Practice Logs: Track their training routines, practice drills, and physical fitness milestones over time.

Past & Upcoming Career History: Preserve a clear timeline of every school or club team represented, past tournaments played, and scheduled upcoming fixtures or trials.

Trophy Cabinet & Highlights: Safely store match videos, tournament photos, medals, certificates, and physical fitness metrics in a dedicated portfolio.

Tournament & Trial Alerts: Discover, track, and register directly for age-group tournaments, school meets, and local trials happening near you.

Scout & Academy Visibility: Present your child's verified profile to certified coaches, sports academies, and club scouts seeking rising grassroots talent.

Verified Endorsements: Back their talent with official coach feedback and academy verifications to ensure credibility.

A profile with just a name and number cannot showcase the hours of discipline and passion your child pours into their sport.

We have attached an example profile so you can see how your child's completed AmaPlayer profile will look.

Help Complete Their Profile Today:
https://amaplayer.com/profile

Every match, run, goal, and medal matters. Let's make sure your child's hard work is recorded, recognized, and remembered for years to come.

Regards,
Team AmaPlayer
Mera Desh, Mera Khel`
      },
      hi: {
        subject: 'अपने बच्चे की AmaPlayer प्रोफ़ाइल पूरी करने में मदद करें',
        body: `प्रिय अभिभावक,

आपके बच्चे ने AmaPlayer पर रजिस्टर करके पहला कदम उठा लिया है, लेकिन उनकी प्रोफ़ाइल अभी अधूरी है। हमारा अनुरोध है कि दो मिनट निकालकर उन्हें प्रोफ़ाइल पूरी करने में मदद करें और उनकी डिजिटल स्पोर्ट्स पहचान सुरक्षित करें।

युवा खिलाड़ी सालों तक अभ्यास करते हैं, प्रतिस्पर्धा करते हैं और अपने स्कूल या अकादमी का प्रतिनिधित्व करते हैं। लेकिन मैच का प्रदर्शन, सर्टिफ़िकेट और उपलब्धियां अक्सर पुराने फ़ोन, स्कोरबुक या भूले हुए फ़ोल्डर में बिखरी रह जाती हैं - और आख़िरकार खो जाती हैं।

AmaPlayer एक स्थायी, वेरिफ़ाइड डिजिटल स्पोर्टिंग पासपोर्ट बनाता है जो आपके बच्चे के पूरे सफर को एक ही जगह सहेजता और प्रस्तुत करता है:

खेल, खेलने की पोज़िशन और मुख्य स्किल्स: अपने बच्चे का मुख्य खेल, मैदान पर भूमिका, प्रमुख हाथ/पैर और मुख्य एथलेटिक ताकत दिखाएं।

लाइव स्कोरिंग और विस्तृत मैच लॉग: आधिकारिक लाइव स्कोरकार्ड, आंकड़ों और हर मैच के प्रदर्शन के साथ वेरिफ़ाइड मैच इतिहास बनाए रखें।

दैनिक ट्रेनिंग और प्रैक्टिस लॉग: उनकी ट्रेनिंग रूटीन, प्रैक्टिस ड्रिल्स और फ़िटनेस उपलब्धियों को समय के साथ ट्रैक करें।

पिछला और आगामी करियर इतिहास: हर स्कूल या क्लब टीम, खेले गए पिछले टूर्नामेंट और तय आगामी मैच या ट्रायल का स्पष्ट रिकॉर्ड सुरक्षित रखें।

ट्रॉफ़ी कैबिनेट और हाइलाइट्स: मैच वीडियो, टूर्नामेंट फ़ोटो, मेडल, सर्टिफ़िकेट और फ़िटनेस मेट्रिक्स एक समर्पित पोर्टफ़ोलियो में सुरक्षित रखें।

टूर्नामेंट और ट्रायल अलर्ट: अपने आस-पास हो रहे आयु-वर्ग टूर्नामेंट, स्कूल मीट और स्थानीय ट्रायल खोजें, ट्रैक करें और सीधे रजिस्टर करें।

स्काउट और अकादमी विज़िबिलिटी: अपने बच्चे की वेरिफ़ाइड प्रोफ़ाइल प्रमाणित कोच, स्पोर्ट्स अकादमियों और उभरती प्रतिभा खोज रहे क्लब स्काउट्स के सामने रखें।

वेरिफ़ाइड एंडोर्समेंट: आधिकारिक कोच फ़ीडबैक और अकादमी वेरिफ़िकेशन से उनकी प्रतिभा को विश्वसनीयता दें।

सिर्फ़ नाम और नंबर वाली प्रोफ़ाइल उन घंटों के अनुशासन और जुनून को नहीं दिखा सकती जो आपका बच्चा अपने खेल में लगाता है।

हमने एक उदाहरण प्रोफ़ाइल संलग्न की है ताकि आप देख सकें कि आपके बच्चे की पूरी AmaPlayer प्रोफ़ाइल कैसी दिखेगी।

आज ही उनकी प्रोफ़ाइल पूरी करने में मदद करें:
https://amaplayer.com/profile

हर मैच, हर रन, हर गोल और हर मेडल मायने रखता है। आइए सुनिश्चित करें कि आपके बच्चे की मेहनत दर्ज हो, पहचानी जाए और आने वाले वर्षों तक याद रखी जाए।

सादर,
टीम AmaPlayer
मेरा देश, मेरा खेल`
      }
    }
  }
];

// Sport values are stored inconsistently across records ("Weight Lifting" vs
// "Weightlifting", "field-events" vs "Field events"), so fold separators before
// comparing. Must stay in sync with normalizeSportValue in emailApi.js.
const normalizeSportValue = (value: string) =>
  String(value || '').toLowerCase().replace(/[\s_-]+/g, '');

const SPORTS_OPTIONS = [...USER_SPORTS_OPTIONS, 'Other', 'Not Specified'];

const ACCOUNT_TYPES = [
  'Parent', 'Athlete', 'Organization', 'Coach'
];

const INDIA_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh', 
  'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 
  'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep', 
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 
  'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const recipientMatchesFilter = (
  recipient: EmailContextResponse['recipientSummary']['users'][number],
  filters: EmailRecipientFilters
) => {
  const hasFlagFilters = filters.includeNotVerified || filters.includeNewUsers || filters.includeVerifiers;
  const hasSportsFilter = Array.isArray(filters.sports) && filters.sports.length > 0;
  const hasAccountTypesFilter = Array.isArray(filters.accountTypes) && filters.accountTypes.length > 0;
  const hasLocationsFilter = Array.isArray(filters.locations) && filters.locations.length > 0;
  const hasLanguagesFilter = Array.isArray(filters.languages) && filters.languages.length > 0;
  const hasAnyFilter = hasFlagFilters || hasSportsFilter || hasAccountTypesFilter || hasLocationsFilter || hasLanguagesFilter;

  if (!hasAnyFilter) return true;

  let matchesFlags = !hasFlagFilters;
  if (hasFlagFilters) {
    if (filters.includeNotVerified && !recipient.isVerified) matchesFlags = true;
    if (filters.includeNewUsers && recipient.isNewUser) matchesFlags = true;
    if (filters.includeVerifiers && recipient.isVerifier) matchesFlags = true;
  }

  let matchesSports = !hasSportsFilter;
  if (hasSportsFilter) {
    const filterSportsLower = filters.sports!.map(normalizeSportValue);
    const recipientSportsLower = (recipient.sports || []).map(normalizeSportValue);
    matchesSports = recipientSportsLower.some(sport => filterSportsLower.includes(sport));
    if (filterSportsLower.includes('notspecified') && recipientSportsLower.length === 0) {
      matchesSports = true;
    }
  }

  let matchesAccountTypes = !hasAccountTypesFilter;
  if (hasAccountTypesFilter) {
    matchesAccountTypes = filters.accountTypes!.includes(recipient.role?.toLowerCase() || 'athlete');
  }

  let matchesLocations = !hasLocationsFilter;
  if (hasLocationsFilter) {
    const filterLocsLower = filters.locations!.map(l => l.toLowerCase());
    const recStateLower = (recipient.state || '').toLowerCase();
    const recCityLower = (recipient.city || '').toLowerCase();
    matchesLocations = filterLocsLower.includes(recStateLower) || filterLocsLower.includes(recCityLower);
  }

  let matchesLanguages = !hasLanguagesFilter;
  if (hasLanguagesFilter) {
    const filterLangs = filters.languages!.map(normalizeRecipientLanguage);
    matchesLanguages = filterLangs.includes(normalizeRecipientLanguage(recipient.language));
  }

  return matchesFlags && matchesSports && matchesAccountTypes && matchesLocations && matchesLanguages;
};

const recipientMatchesSearch = (
  recipient: EmailContextResponse['recipientSummary']['users'][number],
  searchQuery: string
) => {
  const normalizedQuery = String(searchQuery || '').trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  const searchableText = [
    recipient.displayName,
    recipient.email,
    recipient.city,
    recipient.state,
    recipient.location,
    recipient.role
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return searchableText.includes(normalizedQuery);
};

const EmailManagement: React.FC = () => {
  const { currentUser, adminProfile } = useAuth();
  const [context, setContext] = useState<EmailContextResponse | null>(null);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [language, setLanguage] = useState<EmailLanguage>(DEFAULT_EMAIL_LANGUAGE);
  const [sportImage, setSportImage] = useState('');
  const [enableRecipientPersonalization, setEnableRecipientPersonalization] = useState(true);
  const [activeTab, setActiveTab] = useState<EmailCenterTab>('compose');
  const [recipientMode, setRecipientMode] = useState<EmailRecipientMode>('all');
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
  const [singleRecipientId, setSingleRecipientId] = useState('');
  const [recipientFilters, setRecipientFilters] = useState<EmailRecipientFilters>(DEFAULT_RECIPIENT_FILTERS);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [customEmailInput, setCustomEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SendBulkEmailResponse | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset immediately so re-picking the same file still fires onChange.
    event.target.value = '';
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      setError('File size must be under 25MB.');
      return;
    }

    try {
      setUploadingFile(true);
      setError(null);

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const objectKey = `email-attachments/${Date.now()}_${safeName}`;
      const result = await r2Storage.uploadFile(objectKey, file);

      // getPublicUrl falls back to the raw key when REACT_APP_R2_PUBLIC_URL is unset;
      // surface that instead of pasting a non-URL into the email body.
      if (!result.url || !/^https?:\/\//i.test(result.url)) {
        throw new Error('Upload succeeded but no public URL is configured for the R2 bucket. Set REACT_APP_R2_PUBLIC_URL.');
      }

      const attachmentLinkText = `\n\n-------------------------\n📁 Attachment: ${file.name}\n${result.url}`;
      setBody((currentBody) => currentBody + attachmentLinkText);
    } catch (err: any) {
      setError(`Upload failed: ${err?.message || 'Failed to upload attachment.'}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const loadContext = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const nextContext = await emailService.getContext(currentUser, adminProfile);
      setContext(nextContext);
      setSelectedSenderEmail((current) => {
        if (current && nextContext.senderOptions.some((option) => option.email === current)) {
          return current;
        }

        return nextContext.senderOptions[0]?.email || '';
      });
    } catch (serviceError: any) {
      setError(serviceError?.message || 'Failed to load email center.');
    } finally {
      setLoading(false);
    }
  }, [adminProfile, currentUser]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const allRecipients = useMemo(
    () => context?.selectableRecipients || context?.recipientSummary?.users || [],
    [context]
  );
  const availableRecipients = useMemo(
    () => allRecipients.filter((recipient) => recipientMatchesFilter(recipient, recipientFilters)),
    [allRecipients, recipientFilters]
  );
  // Shown next to each language option so an empty audience is obvious before sending.
  const recipientLanguageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    allRecipients.forEach((recipient) => {
      const key = normalizeRecipientLanguage(recipient.language);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [allRecipients]);
  const visibleRecipients = useMemo(
    () => availableRecipients.filter((recipient) => recipientMatchesSearch(recipient, recipientSearchQuery)),
    [availableRecipients, recipientSearchQuery]
  );
  const parsedCustomRecipientEmails = useMemo(
    () => parseCustomRecipientEmails(customEmailInput),
    [customEmailInput]
  );
  const hasActiveRecipientFilters = useMemo(
    () => Object.values(recipientFilters).some(Boolean),
    [recipientFilters]
  );
  const selectedTemplate = useMemo(
    () => FIXED_EMAIL_TEMPLATES.find((template) => template.id === selectedTemplateId) || null,
    [selectedTemplateId]
  );
  const supportsSportImage = useMemo(
    () => Boolean(selectedTemplateId) && (
      (context?.sportImageTemplateIds || SPORT_IMAGE_TEMPLATE_IDS).includes(selectedTemplateId)
    ),
    [context, selectedTemplateId]
  );
  const sportImageOptions = useMemo(
    () => context?.sportImageOptions || SPORT_IMAGE_OPTIONS,
    [context]
  );
  const selectedSportImage = useMemo(
    () => sportImageOptions.find((option) => option.value === sportImage) || null,
    [sportImageOptions, sportImage]
  );
  const selectedTemplateContent = useMemo(
    () => (selectedTemplate
      ? selectedTemplate.content[language] || selectedTemplate.content[DEFAULT_EMAIL_LANGUAGE]
      : null),
    [selectedTemplate, language]
  );

  useEffect(() => {
    const recipientIdSet = new Set(availableRecipients.map((recipient) => recipient.id));

    setSelectedRecipientIds((current) => current.filter((id) => recipientIdSet.has(id)));
    setSingleRecipientId((current) => {
      if (current && recipientIdSet.has(current)) {
        return current;
      }
      return availableRecipients[0]?.id || '';
    });
  }, [availableRecipients]);

  // Never send a sport image for a template that cannot render one.
  useEffect(() => {
    if (!supportsSportImage && sportImage) {
      setSportImage('');
    }
  }, [supportsSportImage, sportImage]);

  const toggleRecipientSelection = (recipientId: string) => {
    setSelectedRecipientIds((current) =>
      current.includes(recipientId)
        ? current.filter((id) => id !== recipientId)
        : [...current, recipientId]
    );
  };

  const selectAllRecipients = (recipientIds: string[]) => {
    setSelectedRecipientIds(recipientIds);
  };

  const applyTemplate = (template: FixedEmailTemplate, targetLanguage: EmailLanguage = language) => {
    const content = template.content[targetLanguage] || template.content[DEFAULT_EMAIL_LANGUAGE];

    setSelectedTemplateId(template.id);
    setSubject(content.subject);
    setBody(content.body);
    setEnableRecipientPersonalization(
      RECIPIENT_PLACEHOLDER_PATTERN.test(content.subject) || RECIPIENT_PLACEHOLDER_PATTERN.test(content.body)
    );
    setError(null);
    setSuccess(null);
  };

  // Switching language re-fills the composer from the selected template so the
  // admin always sees exactly what will be sent. Custom messages are left alone.
  const handleLanguageChange = (nextLanguage: EmailLanguage) => {
    setLanguage(nextLanguage);

    if (selectedTemplate) {
      applyTemplate(selectedTemplate, nextLanguage);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!currentUser) {
      setError('You must be logged in to send emails.');
      return;
    }

    if (!selectedSenderEmail) {
      setError('Select a sender email before sending.');
      return;
    }

    if (!subject.trim() || !body.trim()) {
      setError('Subject and message are required.');
      return;
    }

    if (parsedCustomRecipientEmails.invalid.length > 0) {
      setError(`Invalid custom recipient email(s): ${parsedCustomRecipientEmails.invalid.join(', ')}`);
      return;
    }

    const recipientIds =
      recipientMode === 'all'
        ? []
        : recipientMode === 'single'
          ? (singleRecipientId ? [singleRecipientId] : [])
          : selectedRecipientIds;

    const hasCustomRecipients = parsedCustomRecipientEmails.valid.length > 0;

    if (recipientMode === 'single' && recipientIds.length !== 1 && !hasCustomRecipients) {
      setError('Select one recipient before sending.');
      return;
    }

    if (recipientMode === 'selected' && recipientIds.length === 0 && !hasCustomRecipients) {
      setError('Select at least one recipient before sending.');
      return;
    }

    if (recipientMode === 'all' && (context?.recipientSummary?.totalCount || availableRecipients.length) === 0 && !hasCustomRecipients) {
      setError('No recipients match the selected audience filters.');
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccess(null);
      const recipientsForPersonalization: EmailContextResponse['recipientSummary']['users'] =
        recipientMode === 'all'
          ? availableRecipients
          : recipientIds
              .map((recipientId) => recipientById.get(recipientId))
              .filter((recipient): recipient is EmailContextResponse['recipientSummary']['users'][number] => Boolean(recipient));
      const recipientPersonalization = recipientsForPersonalization.map((recipient) => ({
        id: recipient.id,
        email: recipient.email,
        displayName: recipient.displayName
      }));

      const result = await emailService.sendBulkEmail(currentUser, {
        senderEmail: selectedSenderEmail,
        subject: subject.trim(),
        body: body.trim(),
        templateId: selectedTemplateId || undefined,
        language,
        sportImage: supportsSportImage && sportImage ? sportImage : undefined,
        enableRecipientPersonalization,
        recipientMode,
        recipientIds,
        recipientFilters,
        customRecipientEmails: parsedCustomRecipientEmails.valid,
        recipientPersonalization
      });

      setSuccess(result);
      await loadContext().catch(() => undefined);
      if (selectedTemplate) {
        const content = selectedTemplate.content[language] || selectedTemplate.content[DEFAULT_EMAIL_LANGUAGE];
        setSubject(content.subject);
        setBody(content.body);
      } else {
        setSubject('');
        setBody('');
      }
    } catch (serviceError: any) {
      setError(serviceError?.message || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  const recipientSummary = context?.recipientSummary;
  const visibleRecipientIds = visibleRecipients.map((recipient) => recipient.id);
  const senderOptions = context?.senderOptions || [];
  const noRecipients = availableRecipients.length === 0;
  const noSearchMatches = !noRecipients && visibleRecipients.length === 0;
  const recentSends = context?.recentSends || [];
  const hasCustomRecipients = parsedCustomRecipientEmails.valid.length > 0;
  const selectedRecipientsCount = selectedRecipientIds.length;
  const recipientById = new Map(availableRecipients.map((recipient) => [recipient.id, recipient]));
  const recipientEmailById = new Map(availableRecipients.map((recipient) => [recipient.id, recipient.email]));
  const targetUserEmails = recipientMode === 'all'
    ? availableRecipients.map((recipient) => recipient.email)
    : recipientMode === 'single'
      ? (singleRecipientId ? [recipientEmailById.get(singleRecipientId) || ''] : [])
      : selectedRecipientIds.map((recipientId) => recipientEmailById.get(recipientId) || '');
  const previewTargetSendCount = new Set([
    ...targetUserEmails.filter(Boolean),
    ...parsedCustomRecipientEmails.valid
  ]).size;
  const targetSendCount = recipientMode === 'all' && !hasActiveRecipientFilters
    ? (recipientSummary?.totalCount || 0) + parsedCustomRecipientEmails.valid.length
    : previewTargetSendCount;

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Center</h1>
            <p className="mt-1 text-gray-600">
              {context?.audienceDescription || 'Send email updates to the users available to your role.'}
            </p>
          </div>
          <button
            type="button"
            onClick={loadContext}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Audience
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('compose')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'compose'
                ? 'bg-blue-600 text-white'
                : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Compose
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-blue-600 text-white'
                : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Sent History
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className={`rounded-lg border p-4 text-sm ${
          success.trackingWarning
            ? 'border-amber-200 bg-amber-50 text-amber-800'
            : 'border-green-200 bg-green-50 text-green-700'
        }`}>
          <p>
            Sent from <strong>{success.sender}</strong> to {success.sentCount} user{success.sentCount === 1 ? '' : 's'} in {success.batchCount} batch{success.batchCount === 1 ? '' : 'es'} ({success.recipientMode || recipientMode} mode, {formatLanguageLabel(success.language || language)}).
          </p>
          {success.trackingWarning && (
            <p className="mt-2">
              Email was delivered, but history tracking failed: {success.trackingWarning}
            </p>
          )}
        </div>
      )}

      {activeTab === 'compose' ? (
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr,1.05fr]">
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center gap-3">
              <div className="rounded-full bg-blue-100 p-2">
                <Mail className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Compose Bulk Email</h2>
                <p className="text-sm text-gray-500">Choose all users, a single user, or selected users in your allowed scope.</p>
              </div>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Sender Email</label>
                <select
                  value={selectedSenderEmail}
                  onChange={(event) => setSelectedSenderEmail(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={senderOptions.length === 0 || sending}
                >
                  {senderOptions.length === 0 ? (
                    <option value="">No sender accounts configured</option>
                  ) : (
                    senderOptions.map((option) => (
                      <option key={option.email} value={option.email}>
                        {option.label}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Email Language</label>
                <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 p-3">
                  {EMAIL_LANGUAGE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleLanguageChange(option.value)}
                      disabled={sending}
                      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60 ${
                        language === option.value
                          ? 'border-blue-300 bg-blue-50 text-blue-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Languages className="h-4 w-4" />
                      <span>{option.nativeLabel}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Templates are sent in the selected language. Switching language re-fills the subject and message below.
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-700">Fixed Templates</p>
                  <button
                    type="button"
                    onClick={() => setSelectedTemplateId('')}
                    disabled={sending}
                    className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                  >
                    Use custom message
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {FIXED_EMAIL_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => applyTemplate(template)}
                      disabled={sending}
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        selectedTemplateId === template.id
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 bg-white hover:bg-gray-100'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{template.name}</p>
                      <p className="mt-1 text-xs text-gray-600">{template.description}</p>
                    </button>
                  ))}
                </div>
                {selectedTemplate && selectedTemplateContent && (
                  <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-blue-700">
                      Template Preview ({formatLanguageLabel(language)})
                    </p>
                    <p className="mt-1 text-sm font-medium text-blue-900">{selectedTemplateContent.subject}</p>
                    <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-blue-800">{selectedTemplateContent.body}</p>
                  </div>
                )}
                {supportsSportImage && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                    <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
                      <ImageIcon className="h-4 w-4 text-gray-500" />
                      Embedded Example Profile
                    </label>
                    <select
                      value={sportImage}
                      onChange={(event) => setSportImage(event.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={sending}
                    >
                      <option value="">No image (send without example profile)</option>
                      {sportImageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {language === 'hi' ? `${option.labelHi} (${option.label})` : option.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Optional. Pick a sport to embed its example profile card, or leave unselected to send the email without an image.
                    </p>
                    {selectedSportImage && (
                      <div className="mt-3">
                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                          Image Preview &mdash; {selectedSportImage.label}
                        </p>
                        <img
                          src={selectedSportImage.url}
                          alt={`${selectedSportImage.label} example profile`}
                          className="w-full max-w-[240px] rounded-lg border border-gray-200"
                        />
                        <p className="mt-1 break-all text-[11px] text-gray-400">{selectedSportImage.url}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-2 space-y-1 text-xs text-gray-500">
                  {!selectedTemplate && (
                    <p>Select a template to auto-fill subject and message, then edit if needed before sending.</p>
                  )}
                  <p>Use <code>{'{{name}}'}</code> or <code>{'{{user}}'}</code> in the subject or message to insert the recipient&apos;s name automatically.</p>
                </div>
                <div className="mt-3 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Dynamic recipient name</p>
                    <p className="text-xs text-gray-500">Turn this on to replace placeholders with each recipient&apos;s name.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEnableRecipientPersonalization((current) => !current)}
                    disabled={sending}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      enableRecipientPersonalization
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {enableRecipientPersonalization ? 'On' : 'Off'}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Recipients</label>
                <div className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 sm:grid-cols-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="recipientMode"
                      value="all"
                      checked={recipientMode === 'all'}
                      onChange={() => setRecipientMode('all')}
                      disabled={sending || noRecipients}
                    />
                    <span>All in scope</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="recipientMode"
                      value="single"
                      checked={recipientMode === 'single'}
                      onChange={() => setRecipientMode('single')}
                      disabled={sending || noRecipients}
                    />
                    <span>Single user</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="recipientMode"
                      value="selected"
                      checked={recipientMode === 'selected'}
                      onChange={() => setRecipientMode('selected')}
                      disabled={sending || noRecipients}
                    />
                    <span>Selected users</span>
                  </label>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="mb-2 text-sm font-medium text-gray-700">Audience Filters</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={recipientFilters.includeNotVerified}
                      onChange={(event) =>
                        setRecipientFilters((current) => ({
                          ...current,
                          includeNotVerified: event.target.checked
                        }))
                      }
                      disabled={sending}
                    />
                    <span>Not verified users</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={recipientFilters.includeNewUsers}
                      onChange={(event) =>
                        setRecipientFilters((current) => ({
                          ...current,
                          includeNewUsers: event.target.checked
                        }))
                      }
                      disabled={sending}
                    />
                    <span>New users (24h)</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={recipientFilters.includeVerifiers}
                      onChange={(event) =>
                        setRecipientFilters((current) => ({
                          ...current,
                          includeVerifiers: event.target.checked
                        }))
                      }
                      disabled={sending}
                    />
                    <span>Verifier users</span>
                  </label>
                </div>
                
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Sport</label>
                    <select
                      value={recipientFilters.sports?.[0] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRecipientFilters((current) => ({ ...current, sports: val ? [val] : [] }));
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={sending}
                    >
                      <option value="">No Filter (All Sports)</option>
                      {SPORTS_OPTIONS.map((sport) => (
                        <option key={sport} value={sport}>{sport}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Account Type</label>
                    <select
                      value={recipientFilters.accountTypes?.[0] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRecipientFilters((current) => ({ ...current, accountTypes: val ? [val] : [] }));
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={sending}
                    >
                      <option value="">No Filter (All Types)</option>
                      {ACCOUNT_TYPES.map((type) => (
                        <option key={type.toLowerCase()} value={type.toLowerCase()}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Location (State)</label>
                    <select
                      value={recipientFilters.locations?.[0] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRecipientFilters((current) => ({ ...current, locations: val ? [val] : [] }));
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={sending}
                    >
                      <option value="">No Filter (All Locations)</option>
                      {INDIA_STATES.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">User Language</label>
                    <select
                      value={recipientFilters.languages?.[0] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRecipientFilters((current) => ({ ...current, languages: val ? [val] : [] }));
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={sending}
                    >
                      <option value="">No Filter (All Languages)</option>
                      {RECIPIENT_LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label} ({recipientLanguageCounts.get(option.value) || 0})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <p className="mt-3 text-xs text-gray-500">
                  Active filtered audience: {availableRecipients.length} user{availableRecipients.length === 1 ? '' : 's'}.
                </p>
              </div>

              {recipientMode === 'single' && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Select User</label>
                  <select
                    value={singleRecipientId}
                    onChange={(event) => setSingleRecipientId(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={sending || noRecipients}
                  >
                    {visibleRecipients.length === 0 ? (
                      <option value="">No recipients available</option>
                    ) : (
                      visibleRecipients.map((recipient) => (
                        <option key={recipient.id} value={recipient.id}>
                          {recipient.displayName} ({recipient.email})
                        </option>
                      ))
                    )}
                  </select>
                </div>
              )}

              {recipientMode === 'selected' && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                  <p className="font-medium text-gray-700">
                    {selectedRecipientsCount} user{selectedRecipientsCount === 1 ? '' : 's'} selected
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => selectAllRecipients(visibleRecipientIds)}
                      disabled={sending || visibleRecipientIds.length === 0}
                      className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                    >
                      Select Visible
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedRecipientIds([])}
                      disabled={sending || selectedRecipientsCount === 0}
                      className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Custom Recipient Emails
                </label>
                <textarea
                  value={customEmailInput}
                  onChange={(event) => setCustomEmailInput(event.target.value)}
                  className="min-h-[90px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="extra1@example.com, extra2@example.com"
                  disabled={sending}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Add comma or newline separated email addresses, including addresses outside your user database.
                </p>
                {parsedCustomRecipientEmails.invalid.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">
                    Invalid: {parsedCustomRecipientEmails.invalid.join(', ')}
                  </p>
                )}
                {parsedCustomRecipientEmails.valid.length > 0 && (
                  <p className="mt-1 text-xs text-emerald-700">
                    Custom recipients ready: {parsedCustomRecipientEmails.valid.length}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Team update for registered users"
                  maxLength={180}
                  disabled={sending}
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                    {uploadingFile ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Paperclip className="h-3.5 w-3.5 text-gray-500" />
                        <span>Attach File Link</span>
                      </>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploadingFile || sending}
                    />
                  </label>
                </div>
                <textarea
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  className="min-h-[260px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Write the email body here. Line breaks are preserved."
                  disabled={sending || uploadingFile}
                />
              </div>

              <button
                type="submit"
                disabled={
                  sending ||
                  senderOptions.length === 0 ||
                  (!hasCustomRecipients && noRecipients) ||
                  parsedCustomRecipientEmails.invalid.length > 0 ||
                  targetSendCount === 0
                }
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {sending
                  ? 'Sending...'
                  : `Send to ${targetSendCount} User${targetSendCount === 1 ? '' : 's'}`}
              </button>
            </form>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            <div className="flex items-start gap-3">
              <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-medium">Role-based sender rules are enforced by the server.</p>
                <p className="mt-1">
                  {adminProfile?.role === 'superAdmin'
                    ? 'Super admins can choose any configured sender and reach every user with an email address.'
                    : 'Admins and employees can only send to users within their assigned scope and can only use their own sender address.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Reachable Users</p>
                  <p className="text-3xl font-semibold text-gray-900">{recipientSummary?.totalCount || 0}</p>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Available Senders</p>
                  <p className="text-3xl font-semibold text-gray-900">{senderOptions.length}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Audience Preview</h2>
                <p className="text-sm text-gray-500">
                  Showing {visibleRecipients.length} recipient{visibleRecipients.length === 1 ? '' : 's'} after search out of {availableRecipients.length} filtered users in scope.
                </p>
              </div>
              {recipientSummary?.scope === 'assigned_regions' && recipientSummary.regions.length > 0 && (
                <div className="flex flex-wrap justify-end gap-2">
                  {recipientSummary.regions.map((region) => (
                    <span
                      key={region}
                      className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
                    >
                      {region}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Search Users</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={recipientSearchQuery}
                  onChange={(event) => setRecipientSearchQuery(event.target.value)}
                  placeholder="Search by name, email, city, state, or location"
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {noRecipients || noSearchMatches ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center">
                <Users className="mx-auto h-10 w-10 text-gray-400" />
                <p className="mt-3 text-sm text-gray-500">
                  {allRecipients.length === 0
                    ? 'No users with email addresses are available in your current scope.'
                    : noSearchMatches
                      ? 'No users match the current search.'
                      : 'No users match the current audience filters.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Select</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {visibleRecipients.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={selectedRecipientIds.includes(user.id)}
                            onChange={() => toggleRecipientSelection(user.id)}
                            disabled={sending || noRecipients}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {user.displayName || 'Unnamed User'}
                          <div className="mt-1 flex flex-wrap gap-1">
                            {user.isVerified === false && (
                              <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                                Not Verified
                              </span>
                            )}
                            {user.isNewUser && (
                              <span className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-700">
                                New 24h
                              </span>
                            )}
                            {user.isVerifier && (
                              <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                                Verifier
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatRecipientLocation(user)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {recipientSummary?.truncated && (
              <p className="mt-4 text-xs text-gray-500">
                Preview list is limited by the current response payload. Use Refresh Audience if recipients changed.
              </p>
            )}
          </div>
        </div>
      </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Sent History</h2>
              <p className="text-sm text-gray-500">
                Lightweight delivery history for the latest email batches.
              </p>
            </div>
            <Clock3 className="h-5 w-5 text-gray-400" />
          </div>

          {recentSends.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
              No tracked email sends yet.
            </div>
          ) : (
            <div className="space-y-3">
              {recentSends.map((entry) => (
                <div key={entry.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{entry.subject || 'Untitled email'}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {formatHistoryTime(entry.createdAt)} via {entry.sender || 'Unknown sender'}
                      </p>
                    </div>
                    <div className="text-sm font-medium text-gray-700">
                      {entry.sentCount} sent
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-blue-700">
                      {formatHistoryMode(entry.recipientMode)}
                    </span>
                    <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-violet-700">
                      {formatLanguageLabel(entry.language)}
                    </span>
                    {entry.sportImage && (
                      <span className="rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-orange-700">
                        Image: {formatSportImageLabel(entry.sportImage)}
                      </span>
                    )}
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700">
                      {entry.batchCount} batch{entry.batchCount === 1 ? '' : 'es'}
                    </span>
                    {entry.customRecipientCount > 0 && (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-700">
                        {entry.customRecipientCount} custom
                      </span>
                    )}
                    {entry.scope === 'assigned_regions' && entry.regions.length > 0 && (
                      <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-700">
                        {entry.regions.join(', ')}
                      </span>
                    )}
                    {Array.isArray(entry.recipientFilters?.sports) && entry.recipientFilters.sports.length > 0 && (
                      <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-700">
                        Sports: {entry.recipientFilters.sports.join(', ')}
                      </span>
                    )}
                    {Array.isArray(entry.recipientFilters?.accountTypes) && entry.recipientFilters.accountTypes.length > 0 && (
                      <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-700">
                        Account Types: {entry.recipientFilters.accountTypes.join(', ')}
                      </span>
                    )}
                    {Array.isArray(entry.recipientFilters?.languages) && entry.recipientFilters.languages.length > 0 && (
                      <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-700">
                        User Language: {entry.recipientFilters.languages
                          .map((code) => RECIPIENT_LANGUAGE_OPTIONS.find((o) => o.value === code)?.label || code)
                          .join(', ')}
                      </span>
                    )}
                    {Array.isArray(entry.recipientFilters?.locations) && entry.recipientFilters.locations.length > 0 && (
                      <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-700">
                        Locations: {entry.recipientFilters.locations.join(', ')}
                      </span>
                    )}
                  </div>

                  {entry.sampleRecipients.length > 0 && (
                    <p className="mt-3 text-xs text-gray-500">
                      Sample recipients: {entry.sampleRecipients.join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailManagement;
